import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import styles from "./Promotions.module.css";

const defaultForm = {
  room_type_id: "",
  percentage: "",
  starts_at: "",
  ends_at: "",
  criteria_rules: [],
};

const CRITERIA_OPTIONS = [
  { value: "max_nights", label: "Booking up to number of nights", needsValue: true, valueType: "number", placeholder: "e.g. 5" },
  { value: "requires_weekend", label: "Booking must include weekend", needsValue: false },
  { value: "weekdays_only", label: "Booking weekdays only (no weekend)", needsValue: false },
  { value: "max_amount", label: "Booking total up to amount (NGN)", needsValue: true, valueType: "number", placeholder: "e.g. 250000" },
];

const getCriteriaLabel = (type) => CRITERIA_OPTIONS.find((c) => c.value === type)?.label || type;

const formatCriteriaDisplay = (rules = []) => {
  if (!rules.length) return "-";
  return rules
    .map((rule) => {
      if (rule.type === "max_nights") return `Up to ${rule.value} nights`;
      if (rule.type === "max_amount") return `Total up to NGN ${Number(rule.value || 0).toLocaleString()}`;
      if (rule.type === "requires_weekend") return "Must include weekend";
      if (rule.type === "weekdays_only") return "Weekdays only";
      return getCriteriaLabel(rule.type);
    })
    .join(" + ");
};

const toLocalDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const Promotions = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roomTypes, setRoomTypes] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");
  const [selectedCriterionType, setSelectedCriterionType] = useState("");

  const frontendBase = useMemo(() => {
    const envBase = import.meta.env.VITE_FRONTEND_BASE_URL;
    if (envBase && typeof envBase === "string") {
      return envBase.replace(/\/$/, "");
    }
    return window.location.origin;
  }, []);

  const buildPromoLink = (code) => `${frontendBase}/residencies?promo=${code}`;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [roomRes, promoRes] = await Promise.all([
        api.get("/admin/room-types"),
        api.get("/admin/promotions"),
      ]);
      setRoomTypes(roomRes.data.roomTypes || []);
      setPromotions(promoRes.data.promotions || []);
    } catch (err) {
      setRoomTypes([]);
      setPromotions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onCreatePromotion = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback("");
    try {
      const types = form.criteria_rules.map((r) => r.type);
      if (types.includes("requires_weekend") && types.includes("weekdays_only")) {
        setFeedback("Criteria conflict: cannot require weekend and weekdays-only at the same time.");
        setSaving(false);
        return;
      }

      const hasInvalidRule = form.criteria_rules.some((rule) => {
        if (rule.type === "max_nights" || rule.type === "max_amount") {
          return !rule.value || Number(rule.value) <= 0;
        }
        return false;
      });
      if (hasInvalidRule) {
        setFeedback("Please provide valid values for all numeric criteria.");
        setSaving(false);
        return;
      }

      const payload = {
        room_type_id: Number(form.room_type_id),
        percentage: Number(form.percentage),
        starts_at: form.starts_at,
        ends_at: form.ends_at,
        criteria_rules: form.criteria_rules,
      };

      const res = await api.post("/admin/promotions", payload);
      const created = res.data.promotion;
      setPromotions((prev) => [created, ...prev]);
      setForm(defaultForm);
      setFeedback(`Promotion created. Share this link: ${created.promo_link || buildPromoLink(created.link_code)}`);
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to create promotion.";
      setFeedback(msg);
    } finally {
      setSaving(false);
    }
  };

  const addCriterion = () => {
    if (!selectedCriterionType) return;

    if (form.criteria_rules.some((r) => r.type === selectedCriterionType)) {
      setFeedback("That criterion is already added.");
      return;
    }

    const option = CRITERIA_OPTIONS.find((opt) => opt.value === selectedCriterionType);
    const nextRule = option?.needsValue
      ? { type: selectedCriterionType, value: "" }
      : { type: selectedCriterionType };

    const nextRules = [...form.criteria_rules, nextRule];
    const types = nextRules.map((r) => r.type);
    if (types.includes("requires_weekend") && types.includes("weekdays_only")) {
      setFeedback("Criteria conflict: weekend-required and weekdays-only cannot be combined.");
      return;
    }

    setFeedback("");
    setForm((prev) => ({ ...prev, criteria_rules: nextRules }));
    setSelectedCriterionType("");
  };

  const removeCriterion = (type) => {
    setForm((prev) => ({
      ...prev,
      criteria_rules: prev.criteria_rules.filter((r) => r.type !== type),
    }));
  };

  const updateCriterionValue = (type, value) => {
    setForm((prev) => ({
      ...prev,
      criteria_rules: prev.criteria_rules.map((r) =>
        r.type === type ? { ...r, value } : r
      ),
    }));
  };

  const onToggle = async (id) => {
    try {
      const res = await api.patch(`/admin/promotions/${id}/toggle`);
      setPromotions((prev) => prev.map((p) => (p.id === id ? { ...p, ...res.data.promotion } : p)));
    } catch (err) {
      alert("Could not update promotion status.");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this promotion?")) return;
    try {
      await api.delete(`/admin/promotions/${id}`);
      setPromotions((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      alert("Could not delete promotion.");
    }
  };

  const copyLink = async (code) => {
    const link = buildPromoLink(code);
    try {
      await navigator.clipboard.writeText(link);
      setFeedback(`Copied link: ${link}`);
    } catch {
      setFeedback(`Copy failed. Link: ${link}`);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={styles.contentWrapper}>
        <Header pageTitle="Promotions" onMenuClick={() => setSidebarOpen(true)} />

        <div className={styles.inner}>
          <div className={`${styles.card} ${styles.createCard}`}>
            <h2 className={styles.title}>Create Promotion Link</h2>
            <p className={styles.subtitle}>Select room type, discount percentage, duration, and criteria.</p>

            <form className={styles.form} onSubmit={onCreatePromotion}>
              <label className={styles.label}>Room Type</label>
              <select
                required
                className={styles.input}
                value={form.room_type_id}
                onChange={(e) => onChange("room_type_id", e.target.value)}
              >
                <option value="">Select room type</option>
                {roomTypes.map((rt) => (
                  <option key={rt.id} value={rt.id}>{rt.title}</option>
                ))}
              </select>

              <label className={styles.label}>Promotion Percentage (%)</label>
              <input
                required
                type="number"
                min="1"
                max="100"
                className={styles.input}
                value={form.percentage}
                onChange={(e) => onChange("percentage", e.target.value)}
                placeholder="e.g. 15"
              />

              <div className={styles.grid2}>
                <div>
                  <label className={styles.label}>Start</label>
                  <input
                    required
                    type="datetime-local"
                    className={styles.input}
                    value={form.starts_at}
                    onChange={(e) => onChange("starts_at", e.target.value)}
                  />
                </div>
                <div>
                  <label className={styles.label}>End</label>
                  <input
                    required
                    type="datetime-local"
                    className={styles.input}
                    value={form.ends_at}
                    onChange={(e) => onChange("ends_at", e.target.value)}
                  />
                </div>
              </div>

              <label className={styles.label}>Criteria Rules (optional)</label>
              <div className={styles.criteriaBuilder}>
                <div className={styles.criteriaRow}>
                  <select
                    className={styles.input}
                    value={selectedCriterionType}
                    onChange={(e) => setSelectedCriterionType(e.target.value)}
                  >
                    <option value="">Select criteria event</option>
                    {CRITERIA_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button type="button" className={styles.secondaryBtn} onClick={addCriterion}>
                    Add Rule
                  </button>
                </div>

                {form.criteria_rules.length > 0 && (
                  <div className={styles.criteriaList}>
                    {form.criteria_rules.map((rule) => {
                      const option = CRITERIA_OPTIONS.find((opt) => opt.value === rule.type);
                      return (
                        <div key={rule.type} className={styles.criteriaItem}>
                          <span className={styles.criteriaLabel}>{getCriteriaLabel(rule.type)}</span>
                          {option?.needsValue && (
                            <input
                              type={option.valueType || "text"}
                              className={styles.criteriaValueInput}
                              value={rule.value ?? ""}
                              onChange={(e) => updateCriterionValue(rule.type, e.target.value)}
                              placeholder={option.placeholder || "Enter value"}
                              min="1"
                            />
                          )}
                          <button
                            type="button"
                            className={styles.removeBtn}
                            onClick={() => removeCriterion(rule.type)}
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <button className={styles.primaryBtn} type="submit" disabled={saving}>
                {saving ? "Creating..." : "Create Promotion"}
              </button>
            </form>

            {feedback && <p className={styles.feedback}>{feedback}</p>}
          </div>

          <div className={`${styles.card} ${styles.listCard}`}>
            <h2 className={styles.title}>Active and Past Promotions</h2>
            {loading ? (
              <p className={styles.empty}>Loading promotions...</p>
            ) : promotions.length === 0 ? (
              <p className={styles.empty}>No promotions yet.</p>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Room Type</th>
                      <th>% Off</th>
                      <th>Criteria</th>
                      <th>Duration</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promotions.map((promo) => (
                      <tr key={promo.id}>
                        <td>{promo.room_type?.title || "Room"}</td>
                        <td>{promo.percentage}%</td>
                        <td>{promo.criteria_display || formatCriteriaDisplay(promo.criteria_rules || [])}</td>
                        <td>{toLocalDateTime(promo.starts_at)} to {toLocalDateTime(promo.ends_at)}</td>
                        <td>
                          <span className={promo.is_active ? styles.statusActive : styles.statusInactive}>
                            {promo.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className={styles.actionRow}>
                            <button type="button" className={styles.smallBtn} onClick={() => copyLink(promo.link_code)}>Copy Link</button>
                            <button type="button" className={styles.smallBtn} onClick={() => onToggle(promo.id)}>
                              {promo.is_active ? "Deactivate" : "Activate"}
                            </button>
                            <button type="button" className={styles.dangerBtn} onClick={() => onDelete(promo.id)}>Delete</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {sidebarOpen && <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

export default Promotions;
